// src/components/CrmSystem.jsx
import React, { useState, useEffect } from "react";
import {
  getAllPersons,
  createPerson,
  updatePerson,
  deletePerson,
} from "../services/azurePersonsService";
import {
  getAllPartners,
  createPartner,
  updatePartner,
  deletePartner,
} from "../services/azurePartnersService";
import CrmDetailModal from "./CrmDetailModal";
import * as XLSX from "xlsx";

const CrmSystem = ({ initialTab }) => {
  const [activeTab, setActiveTab] = useState(initialTab || "persons");
  const [persons, setPersons] = useState([]);
  const [partners, setPartners] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("view"); // 'view', 'edit', 'create'

  useEffect(() => {
    if (initialTab && initialTab !== activeTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [personsData, partnersData] = await Promise.all([
        getAllPersons(),
        getAllPartners(),
      ]);

      // WICHTIG: Zuerst Partners verarbeiten, damit sie für Personen verfügbar sind
      const partnersWithCrm = partnersData.map((partner) => ({
        ...partner,
        email: partner.email || "",
        phone: partner.phone || "",
        address: {
          strasse: partner.addressStrasse || partner.address || "",
          plz: partner.addressPlz || "",
          ort: partner.addressOrt || "",
          land: partner.addressLand || "Schweiz",
        },
        city: partner.addressOrt || "",
        tags: partner.tags
          ? typeof partner.tags === "string"
            ? partner.tags
                .split(",")
                .map((t) => t.trim())
                .filter((t) => t)
            : partner.tags
          : [],
        notes: partner.notes || "",
      }));

      // Lade Relations für Projektsuche
      let relations = [];
      let projects = [];
      try {
        const RelationsService = await import(
          "../services/azureRelationsService"
        );
        const ProjectsService = await import(
          "../services/azureProjectsService"
        );
        [relations, projects] = await Promise.all([
          RelationsService.getAllRelations(),
          ProjectsService.getAllProjects(),
        ]);
      } catch (err) {
        console.warn("Fehler beim Laden von Relations/Projects:", err);
      }

      // Dann Personen verarbeiten mit Zugriff auf partnersWithCrm
      const personsWithCrm = personsData.map((person) => {
        // Prüfe ob Person eigene Adresse hat
        const hasOwnAddress =
          person.addressStrasse || person.addressPlz || person.addressOrt;

        let finalAddress = {
          strasse: person.addressStrasse || "",
          plz: person.addressPlz || "",
          ort: person.addressOrt || "",
          land: person.addressLand || "Schweiz",
        };

        // Wenn keine eigene Adresse und Organisation vorhanden, nutze Organisation-Adresse
        if (!hasOwnAddress && person.organization) {
          const org = partnersWithCrm.find(
            (p) => p.name === person.organization
          );
          if (org && org.address) {
            finalAddress = {
              strasse: org.address.strasse || "",
              plz: org.address.plz || "",
              ort: org.address.ort || "",
              land: org.address.land || "Schweiz",
            };
          }
        }

        // Finde zugeordnete Projekte für Suche
        const personRelations = relations.filter(
          (r) =>
            r.targetType === "person" &&
            r.targetId === person.id &&
            r.sourceType === "project"
        );
        const personProjects = personRelations
          .map((rel) => {
            const project = projects.find((p) => p.id === rel.sourceId);
            return project ? project.code : null;
          })
          .filter((code) => code);

        return {
          ...person,
          organization: person.organization || "",
          position: person.position || "",
          anrede: person.anrede || "du",
          geburtsdatum: person.geburtsdatum || "",
          mobile: person.mobile || "",
          phone: person.phone || "",
          newsletter:
            person.newsletter === "true" || person.newsletter === true || false,
          weihnachtskarte:
            person.weihnachtskarte === "true" ||
            person.weihnachtskarte === true ||
            false,
          address: finalAddress,
          hasOwnAddress: hasOwnAddress, // Markierung für späteren Gebrauch
          tags: person.tags
            ? typeof person.tags === "string"
              ? person.tags
                  .split(",")
                  .map((t) => t.trim())
                  .filter((t) => t)
              : person.tags
            : [],
          projectCodes: personProjects,
        };
      });

      // Füge auch Projektcodes zu Partnern hinzu
      const partnersWithProjects = partnersWithCrm.map((partner) => {
        const partnerRelations = relations.filter(
          (r) =>
            r.targetType === "partner" &&
            r.targetId === partner.id &&
            r.sourceType === "project"
        );
        const partnerProjects = partnerRelations
          .map((rel) => {
            const project = projects.find((p) => p.id === rel.sourceId);
            return project ? project.code : null;
          })
          .filter((code) => code);

        return {
          ...partner,
          projectCodes: partnerProjects,
        };
      });

      setPersons(personsWithCrm);
      setPartners(partnersWithProjects);
      setTags([]);
    } catch (err) {
      setError("Fehler beim Laden der Daten: " + err.message);
      console.error("Load error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDetail = (contact, type, mode = "view") => {
    setSelectedContact({ ...contact, type });
    setModalMode(mode);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedContact(null);
    setModalMode("view");
  };

  const handleSaveContact = async (
    contactData,
    selectedProjects,
    projectRoles
  ) => {
    try {
      setLoading(true);

      // Erweitere die Daten für die bestehenden Services
      const extendedData = {
        ...contactData,
        // CRM-Felder als separate Properties für Azure
        organization: contactData.organization || "",
        anrede: contactData.anrede || "du",
        geburtsdatum: contactData.geburtsdatum || "",
        mobile: contactData.mobile || "",
        phone: contactData.phone || "",
        newsletter:
          contactData.newsletter === true || contactData.newsletter === "true",
        weihnachtskarte:
          contactData.weihnachtskarte === true ||
          contactData.weihnachtskarte === "true",
        addressStrasse: contactData.address?.strasse || "",
        addressPlz: contactData.address?.plz || "",
        addressOrt: contactData.address?.ort || "",
        addressLand: contactData.address?.land || "Schweiz",
        tags: contactData.tags ? contactData.tags.join(", ") : "",
      };

      let savedContact;
      if (modalMode === "create") {
        if (contactData.type === "person") {
          savedContact = await createPerson(extendedData);
        } else {
          savedContact = await createPartner(extendedData);
        }
        setSuccess("Kontakt erfolgreich erstellt!");
      } else if (modalMode === "edit") {
        if (contactData.type === "person") {
          savedContact = await updatePerson(contactData.id, extendedData);
        } else {
          savedContact = await updatePartner(contactData.id, extendedData);
        }
        setSuccess("Kontakt erfolgreich aktualisiert!");
      }

      // Projekt-Relationen aktualisieren
      if (selectedProjects && savedContact) {
        const RelationsService = await import(
          "../services/azureRelationsService"
        );

        // Lösche bestehende Projekt-Relationen
        const existingRelations = await RelationsService.getAllRelations();
        const targetType = contactData.type === "person" ? "person" : "partner";
        const oldRelations = existingRelations.filter(
          (r) =>
            r.targetType === targetType &&
            r.targetId === (savedContact.id || contactData.id) &&
            r.sourceType === "project"
        );

        for (const rel of oldRelations) {
          await RelationsService.deleteRelation(rel.id);
        }

        // Erstelle neue Relationen
        for (const [projectId, isSelected] of Object.entries(
          selectedProjects
        )) {
          if (isSelected) {
            const relationType =
              contactData.type === "person"
                ? RelationsService.RELATION_TYPES.PROJECT_PERSON
                : RelationsService.RELATION_TYPES.PROJECT_PARTNER;

            await RelationsService.createRelation({
              sourceType: "project",
              sourceId: projectId,
              targetType: targetType,
              targetId: savedContact.id || contactData.id,
              relationType: relationType,
              function: projectRoles[projectId] || "",
              description: `${
                contactData.type === "person" ? "Person" : "Organisation"
              } in Projekt`,
              visibility: contactData.type === "person" ? "false" : "true",
            });
          }
        }
      }

      // Reload data to get latest state
      await loadInitialData();
      handleCloseModal();
    } catch (err) {
      setError("Fehler beim Speichern: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteContact = async (contact) => {
    if (
      !window.confirm(
        `Sind Sie sicher, dass Sie ${
          contact.type === "person" ? "diese Person" : "diese Organisation"
        } löschen möchten?`
      )
    ) {
      return;
    }

    try {
      setLoading(true);

      if (contact.type === "person") {
        await deletePerson(contact.id);
        setPersons((prev) => prev.filter((p) => p.id !== contact.id));
      } else {
        await deletePartner(contact.id);
        setPartners((prev) => prev.filter((p) => p.id !== contact.id));
      }

      setSuccess("Kontakt erfolgreich gelöscht!");
      handleCloseModal();
    } catch (err) {
      setError("Fehler beim Löschen: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const filterContacts = (contacts) => {
    if (!searchTerm && !tagFilter) return contacts;

    return contacts.filter((contact) => {
      // Normale Suche
      const searchMatch =
        !searchTerm ||
        (contact.firstName &&
          contact.firstName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (contact.lastName &&
          contact.lastName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (contact.name &&
          contact.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (contact.email &&
          contact.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (contact.organization &&
          contact.organization
            .toLowerCase()
            .includes(searchTerm.toLowerCase())) ||
        // Projektcode-Suche
        (contact.projectCodes &&
          contact.projectCodes.some((code) =>
            code.toLowerCase().includes(searchTerm.toLowerCase())
          ));

      const tagMatch =
        !tagFilter || (contact.tags && contact.tags.includes(tagFilter));

      return searchMatch && tagMatch;
    });
  };

  const handleExportToExcel = () => {
    try {
      const contactsToExport =
        activeTab === "persons"
          ? filterContacts(persons)
          : filterContacts(partners);

      if (contactsToExport.length === 0) {
        alert("Keine Kontakte zum Exportieren vorhanden");
        return;
      }

      const excelData = contactsToExport.map((contact) => {
        if (activeTab === "persons") {
          // Person: Nutze die bereits geladene Adresse (mit Fallback)
          const addr = contact.address || {};

          return {
            Vorname: contact.firstName || "",
            Nachname: contact.lastName || "",
            Organisation: contact.organization || "",
            Position: contact.position || "",
            Strasse: addr.strasse || "",
            PLZ: addr.plz || "",
            Ort: addr.ort || "",
            Land: addr.land || "",
            Email: contact.email || "",
            Telefon: contact.phone || "",
            Mobile: contact.mobile || "",
            Geburtsdatum: contact.geburtsdatum || "",
            Newsletter: contact.newsletter ? "Ja" : "Nein",
            Weihnachtskarte: contact.weihnachtskarte ? "Ja" : "Nein",
            Anrede: contact.anrede || "",
            Tags: contact.tags ? contact.tags.join(", ") : "",
            Notizen: contact.notes || "",
          };
        } else {
          // Organisation
          const addr = contact.address || {};
          return {
            Name: contact.name || "",
            Typ: contact.type || "",
            Strasse: addr.strasse || "",
            PLZ: addr.plz || "",
            Ort: addr.ort || "",
            Land: addr.land || "",
            Email: contact.email || "",
            Telefon: contact.phone || "",
            Website: contact.website || "",
            Tags: contact.tags ? contact.tags.join(", ") : "",
            Notizen: contact.notes || "",
          };
        }
      });

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();

      const sheetName = activeTab === "persons" ? "Personen" : "Organisationen";
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

      const columnWidths = Object.keys(excelData[0]).map((key) => ({
        wch: Math.max(key.length, 15),
      }));
      worksheet["!cols"] = columnWidths;

      const today = new Date().toISOString().split("T")[0];
      const fileName = `CRM_${sheetName}_${today}.xlsx`;

      XLSX.writeFile(workbook, fileName);

      setSuccess(`${contactsToExport.length} Kontakte erfolgreich exportiert!`);
    } catch (err) {
      setError("Fehler beim Export: " + err.message);
      console.error("Export error:", err);
    }
  };

  const handleExportWeihnachtskarten = () => {
    try {
      // Filter persons who should receive Christmas cards
      const weihnachtskartenPersons = persons.filter((person) => person.weihnachtskarte);

      if (weihnachtskartenPersons.length === 0) {
        alert("Keine Personen mit Weihnachtskarten-Markierung gefunden");
        return;
      }

      // Create Excel data with Organisation, Person name, Address
      const excelData = weihnachtskartenPersons.map((person) => {
        const addr = person.address || {};
        const fullName = [person.firstName, person.lastName].filter(Boolean).join(" ");

        // Format address as single string
        const addressParts = [
          addr.strasse,
          [addr.plz, addr.ort].filter(Boolean).join(" "),
          addr.land && addr.land !== "Schweiz" ? addr.land : null
        ].filter(Boolean);
        const fullAddress = addressParts.join(", ");

        return {
          "Organisation": person.organization || "",
          "Name": fullName,
          "Adresse": fullAddress,
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Weihnachtskarten");

      // Set column widths
      worksheet["!cols"] = [
        { wch: 30 }, // Organisation
        { wch: 25 }, // Name
        { wch: 50 }, // Adresse
      ];

      const today = new Date().toISOString().split("T")[0];
      const fileName = `Weihnachtskarten_${today}.xlsx`;

      XLSX.writeFile(workbook, fileName);

      setSuccess(`${weihnachtskartenPersons.length} Weihnachtskarten exportiert!`);
    } catch (err) {
      setError("Fehler beim Export: " + err.message);
      console.error("Weihnachtskarten export error:", err);
    }
  };

  const getContactsWithStats = () => {
    const totalPersons = persons.length;
    const totalPartners = partners.length;
    const totalNewsletter = persons.filter((p) => p.newsletter).length;

    return { totalPersons, totalPartners, totalNewsletter };
  };

  const { totalPersons, totalPartners, totalNewsletter } =
    getContactsWithStats();

  return (
    <div className="crm-container">
      {/* Header */}
      <div className="crm-header">
        <h1>CRM System</h1>

        {/* Search and Filter Bar */}
        <div className="search-bar">
          <div className="search-controls">
            <input
              type="text"
              className="search-input"
              placeholder="Suche nach Name, Email, Projektcode..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select
              className="filter-select"
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
            >
              <option value="">Alle Tags</option>
              {/* Dynamische Tags aus allen Kontakten - alphabetisch sortiert */}
              {[
                ...new Set(
                  [...persons, ...partners].flatMap((c) => c.tags || [])
                ),
              ]
                .sort((a, b) => a.localeCompare(b))
                .map((tag) => (
                  <option key={tag} value={tag}>
                    {tag}
                  </option>
                ))}
            </select>
          </div>
          <button
            className="btn btn-success export-btn"
            onClick={handleExportToExcel}
            title="Aktuelle Ansicht als Excel exportieren"
          >
            Export Excel
          </button>
          <button
            className="btn btn-secondary export-btn"
            onClick={handleExportWeihnachtskarten}
            title="Personen mit Weihnachtskarten-Markierung exportieren"
          >
            Export Weihnachtskarten
          </button>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}
      {success && (
        <div className="alert alert-success">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)}>&times;</button>
        </div>
      )}

      {/* Tab Content */}
      <div className="tab-content">
        {loading && <div className="loading">Lade...</div>}

        {!loading && (
          <>
            {/* Persons Tab */}
            {activeTab === "persons" && (
              <div className="table-container">
                <table className="contact-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Kontakt</th>
                      <th>Organisation</th>
                      <th>Status</th>
                      <th>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filterContacts(persons).map((person) => (
                      <tr key={person.id}>
                        <td>
                          <div className="contact-name">
                            {person.firstName} {person.lastName}
                          </div>
                          <div className="contact-subtitle">
                            {person.anrede || "Du"}
                          </div>
                        </td>
                        <td>
                          <div>{person.email || "-"}</div>
                          <div className="contact-subtitle">
                            {person.phone || person.mobile || "-"}
                          </div>
                        </td>
                        <td>{person.organization || "-"}</td>
                        <td>
                          <div className="status-indicators">
                            {person.newsletter && (
                              <div
                                className="status-badge newsletter"
                                title="Newsletter"
                              >
                                N
                              </div>
                            )}
                            {person.weihnachtskarte && (
                              <div
                                className="status-badge card"
                                title="Weihnachtskarte"
                              >
                                W
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="contact-actions-inline">
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() =>
                                handleOpenDetail(person, "person", "view")
                              }
                            >
                              Details
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() =>
                                handleOpenDetail(person, "person", "edit")
                              }
                            >
                              Bearbeiten
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === "organizations" && (
              <div className="table-container">
                <table className="contact-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Kontakt</th>
                      <th>Ort</th>
                      <th>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filterContacts(partners).map((partner) => (
                      <tr key={partner.id}>
                        <td>
                          <div className="contact-name">{partner.name}</div>
                          <div className="contact-subtitle">
                            {partner.type || "Organisation"}
                          </div>
                        </td>
                        <td>
                          <div>{partner.email || "-"}</div>
                          <div className="contact-subtitle">
                            {partner.phone || "-"}
                          </div>
                        </td>
                        <td>{partner.city || "-"}</td>
                        <td>
                          <div className="contact-actions-inline">
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() =>
                                handleOpenDetail(
                                  partner,
                                  "organization",
                                  "view"
                                )
                              }
                            >
                              Details
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() =>
                                handleOpenDetail(
                                  partner,
                                  "organization",
                                  "edit"
                                )
                              }
                            >
                              Bearbeiten
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Contact Button */}
      <button
        className="add-contact-btn"
        onClick={() =>
          handleOpenDetail(
            null,
            activeTab === "persons" ? "person" : "organization",
            "create"
          )
        }
        title="Neuen Kontakt hinzufügen"
      >
        +
      </button>

      {/* Detail Modal */}
      {modalOpen && (
        <CrmDetailModal
          contact={selectedContact}
          mode={modalMode}
          partners={partners}
          tags={tags}
          onSave={handleSaveContact}
          onDelete={handleDeleteContact}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default CrmSystem;
